<template>
  <div ref="bannerContainer">
    <v-banner
      transition="slide-y-transition"
      class="banner free-plan-banner"
      color="rgba(255, 219, 77, 0.5)"
      v-if="activeBanner === 'free-plan' && showBanner">
      <v-row @click="goToUpgrade('banner_expired')" class="banner-row" align="center">
        <v-col align="start" class="py-5 banner-text">
         {{ $t(hasInactiveCodesToRestore ? 'banner.in_free_plan' : 'banner.in_free_plan_no_codes') }}
        </v-col>
        <button class="action-btn trial-action-btn mr-2">
          {{ $t(hasInactiveCodesToRestore ? 'banner.restore_service' : 'banner.upgrade_now') }}
        </button>
        <v-btn
          icon
          @click="close"
        >
          <v-icon class="icon">$close</v-icon>
        </v-btn>
      </v-row>
    </v-banner>
    <v-banner
      transition="slide-y-transition"
      class="banner trial-banner"
      :class="{ 'trial-ending-banner': isTrialEndingSoon }"
      v-if="activeBanner === 'trial' && showBanner">
      <v-row @click="goToUpgrade('banner_trial')" class="banner-row" align="center">
        <v-col align="start" class="py-5 banner-text">
          <span v-html="$tc(trialBannerMessage, trialRemainingDays, { days: trialRemainingDays })"></span>
        </v-col>
        <button class="action-btn trial-action-btn mr-2">
          {{ $t('banner.upgrade_now') }}
        </button>
        <v-btn
          icon
          @click="close"
        >
          <v-icon class="icon">$close</v-icon>
        </v-btn>
      </v-row>
    </v-banner>

    <!-- ending_soon -->
    <v-banner
      transition="slide-y-transition"
      class="banner trial-banner"
      :class="{ 'subscription-ending-banner': isSubscriptionEndingSoon }"
      color="rgba(255, 219, 77, 0.5)"
      v-if="activeBanner === 'subscription-ending' && showBanner">
      <v-row @click="goToProfile()" class="banner-row" align="center">
        <v-col align="start" class="py-5 banner-text">
          <span v-html="$t('banner.ending_soon', { date: formatDate(currentPlan.valid_to) })"></span>
        </v-col>
        <button class="action-btn trial-action-btn mr-2">
          {{ $t('banner.resume_subscription') }}
        </button>
        <v-btn
          icon
          @click="close"
        >
          <v-icon class="icon">$close</v-icon>
        </v-btn>
      </v-row>
    </v-banner>

    <!-- Payment past due -->
    <v-banner
      transition="slide-y-transition"
      class="banner payment-past-due-banner"
      v-if="activeBanner === 'payment-past-due' && showBanner">
      <v-row @click="goToProfile()" class="banner-row" align="center">
        <v-col align="start" class="py-5 banner-text">
          <span v-html="$t('banner.payment_past_due')"></span>
        </v-col>
        <button class="action-btn trial-action-btn mr-2">
          {{ $t('banner.update_billing') }}
        </button>
        <v-btn
          icon
          @click="close"
        >
          <v-icon class="icon">$close</v-icon>
        </v-btn>
      </v-row>
    </v-banner>

    <!-- Upcoming renewal -->
    <v-banner
      transition="slide-y-transition"
      class="banner upcoming-renewal-banner"
      color="rgba(255, 219, 77, 0.5)"
      v-if="activeBanner === 'upcoming-renewal' && showBanner">
      <v-row @click="goToProfile()" class="banner-row" align="center">
        <v-col align="start" class="py-5 banner-text">
          <span v-html="$t('banner.upcoming_renewal', { date: formatDate(currentPlan.valid_to) })"></span>
        </v-col>
        <button class="action-btn trial-action-btn mr-2">
          {{ $t('banner.update_billing') }}
        </button>
        <v-btn
          icon
          @click="close"
        >
          <v-icon class="icon">$close</v-icon>
        </v-btn>
      </v-row>
    </v-banner>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import Utils from '@/utils/utils'

export default {
  computed: {
    ...mapGetters(['currentPlan', 'totalBlacklistedAssignedCodesCount', 'subscription']),

    isTrialPlan () {
      return this.currentPlan?.id === 'st-pro-trial'
    },

    isFreeBillingPlan () {
      return this.currentPlan?.id === 'st-pro-free'
    },

    hasInactiveCodesToRestore () {
      return this.totalBlacklistedAssignedCodesCount > 0
    },

    isPaidPlan () {
      const planId = this.currentPlan?.id || ''

      return !this.isTrialPlan && !planId.startsWith('st-pro-free')
    },

    isInGracePeriod () {
      if (!this.currentPlan.cancellation_date) {
        return false
      }

      const now = new Date()
      const expiryDate = new Date(this.currentPlan.valid_to)
      const cancellationDate = new Date(this.currentPlan.cancellation_date)

      return now.getTime() < cancellationDate.getTime() && now.getTime() >= expiryDate.getTime()
    },

    trialRemainingDays () {
      return Utils.getRemainingTrialDays(this.currentPlan?.valid_to)
    },

    isTrialEndingSoon () {
      return this.trialRemainingDays <= 3
    },

    trialBannerMessage () {
      // Trial expired but the backend hasn't downgraded the account yet
      if (this.trialRemainingDays === 0) {
        return 'banner.trial_ends_today'
      }

      return this.isTrialEndingSoon ? 'banner.trial_ending_soon' : 'banner.trial_active'
    },

    hasLessThanTenDaysRemaining () {
      const validToDate = new Date(this.currentPlan?.valid_to)

      if (Number.isNaN(validToDate.getTime())) {
        return false
      }

      const millisecondsInTenDays = 1000 * 60 * 60 * 24 * 10
      const remainingMilliseconds = validToDate.getTime() - Date.now()

      return remainingMilliseconds > 0 && remainingMilliseconds < millisecondsInTenDays
    },

    isSubscriptionEndingSoon () {
      //  If not on trial, has cancelation date and is on a paid plan and less than 10 days remaining
      return this.isPaidPlan && !!this.currentPlan?.cancellation_date && this.hasLessThanTenDaysRemaining
    },

    isPaymentPastDue () {
      return this.isPaidPlan &&
        this.currentPlan?.status === 'past_due' &&
        this.subscription?.collection_mode === 'automatic'
    },

    isUpcomingRenewal () {
      // If not trial and no cancellation and paid plan (not free or freemium) and less than 10 days
      return this.isPaidPlan && !this.currentPlan?.cancellation_date && this.hasLessThanTenDaysRemaining
    },

    activeBanner () {
      if (this.isTrialPlan) {
        return 'trial'
      }

      if (this.isPaymentPastDue) {
        return 'payment-past-due'
      }

      if (this.isSubscriptionEndingSoon) {
        return 'subscription-ending'
      }

      if (this.isUpcomingRenewal) {
        return 'upcoming-renewal'
      }

      if (this.isFreeBillingPlan) {
        return 'free-plan'
      }

      return null
    }
  },
  data () {
    return {
      showBanner: true,
      resizeObserver: null
    }
  },
  methods: {
    ...mapMutations({
      setBannerVisibility: 'SET_BANNER_VISIBILITY'
    }),

    close () {
      this.showBanner = false
      this.setBannerVisibility(false)
      this.$nextTick(() => this.updateBannerHeightVar())
    },
    goToUpgrade (source) {
      this.$router.push({ name: 'upgrade', query: { source } })
    },
    formatDate (date) {
      return Utils.formatDate(date, true)
    },
    goToProfile () {
      this.$router.push({ name: 'profile.subscription' })
    },
    updateBannerHeightVar () {
      const el = this.$refs.bannerContainer
      const height = el ? el.offsetHeight : 0
      document.documentElement.style.setProperty('--banner-height', `${height}px`)
    }
  },
  created () {
    if (this.activeBanner) {
      this.setBannerVisibility(true)
    }
  },
  mounted () {
    this.updateBannerHeightVar()
    if (typeof ResizeObserver !== 'undefined' && this.$refs.bannerContainer) {
      this.resizeObserver = new ResizeObserver(() => this.updateBannerHeightVar())
      this.resizeObserver.observe(this.$refs.bannerContainer)
    }
  },
  beforeDestroy () {
    document.documentElement.style.setProperty('--banner-height', '0px')
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }
}
</script>

<style lang="sass" scoped>
.banner
  --banner-bg: #FFDB4D
  --banner-bg-opacity: 0.5
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)
  cursor: pointer
  background: var(--banner-bg) !important
  text-align: center
  .banner-text
    color: var(--banner-text)
  .v-banner__actions
    flex: 0 !important
    margin-left: 20px
    .icon
      color: #323232

.free-plan-banner
  --banner-bg: #F22A42
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.migration-banner
  --banner-bg: #FF7F00
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.trial-banner
  --banner-bg: #15BBF0
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.trial-ending-banner
  --banner-bg: #FF7F00
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.subscription-ending-banner
  --banner-bg: #15BBF0
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.upcoming-renewal-banner
  --banner-bg: #15BBF0
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.payment-past-due-banner
  --banner-bg: #F22A42
  --banner-text: #FFF
  --action-bg: var(--banner-text)
  --action-text: var(--banner-bg)

.action-btn
  background-color: var(--action-bg) !important
  border-radius: 5px
  box-shadow: none !important
  color: var(--action-text) !important
  font-size: 14px
  font-weight: 700
  letter-spacing: 0
  line-height: 20px
  padding: 4px 18px !important
  text-transform: none

.trial-action-btn
  background-color: var(--action-bg) !important
  color: var(--action-text) !important

</style>
